import { Controller } from 'react-hook-form';
import { InputText, Select, Autocomplete } from '@ui/src';
import { useAppSelector } from '@store';
import ItemFormBlock from './item-form-block';
import ItemFormFlexInputs from './item-form-flex-inputs';
import { CategoryLevelEnum } from '../../../hoc/types';
import { ItemFormTypes } from './types';

import cn from './form.module.css';

let timeout: NodeJS.Timeout;

const getRiskScoreName = (name: string) => {
  let riskScoreName;

  switch (name) {
    case CategoryLevelEnum.LOW:
      riskScoreName = 'Low';
      break;
    case CategoryLevelEnum.MEDIUM:
      riskScoreName = 'Medium';
      break;
    case CategoryLevelEnum.HIGH:
      riskScoreName = 'High';
      break;
    default:
      riskScoreName = name;
      break;
  }

  return riskScoreName;
};

const ItemForm = ({
  control,
  errors,
  isEditable,
  itemId,
  currencies,
  currenciesList,
  currencyId,
  baseCurrency,
  setValue,
}: ItemFormTypes) => {
  const { riskScoreRanges } = useAppSelector((state) => state.amlDictionaries);

  return (
    <div className={cn.itemFormWrapper}>
      <ItemFormBlock title="Risk category">
        <>
          <Controller
            control={control}
            name="riskCategory"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Select
                name="riskCategory"
                placeholder="Select Risk category"
                onChange={(e) => {
                  onChange(e);

                  const currentRange = riskScoreRanges?.find(
                    (range) => range.name === e.target.value,
                  );

                  if (currentRange && setValue) {
                    setValue('riskScore', currentRange.from.toString());
                  }
                }}
                value={value}
                selectWrapperClassName={`${cn.selectRiskCategory} ${
                  errors?.riskCategory?.message ? cn.selectError : ''
                }`}
                list={riskScoreRanges?.map((item) => ({
                  name: getRiskScoreName(item.name),
                  value: item.name,
                }))}
                disabled={Boolean(!isEditable && itemId)}
                tabIndex={!isEditable && itemId ? -1 : 0}
                fullWidth
              />
            )}
          />
          {errors?.riskCategory?.message && (
            <div className={cn.error}>{errors?.riskCategory?.message}</div>
          )}
        </>
      </ItemFormBlock>
      <ItemFormBlock title="Risk score">
        <Controller
          control={control}
          name="riskScore"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="riskScore"
              placeholder="Enter risk score"
              onChange={(e) => {
                clearTimeout(timeout);

                onChange(e.target.value);

                timeout = setTimeout(() => {
                  const currentRange = riskScoreRanges?.find(
                    (item) =>
                      item.from <= Number(e.target.value) &&
                      item.to >= Number(e.target.value),
                  );

                  if (setValue) {
                    setValue('riskCategory', currentRange?.name as string);
                  }
                }, 300);
              }}
              value={value}
              error={errors.riskScore?.message}
              disabled={Boolean(!isEditable && itemId)}
              tabIndex={!isEditable && itemId ? -1 : 0}
            />
          )}
        />
      </ItemFormBlock>
      <ItemFormBlock title="Currency">
        <>
          <Controller
            control={control}
            name="currencyId"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                name="currencyId"
                placeholder="Select currency"
                onChange={onChange}
                value={value}
                selectWrapperClassName={`${cn.selectRiskCategory} ${
                  errors?.currencyId?.message ? cn.selectError : ''
                }`}
                list={currencies}
                tabIndex={-1}
                fullWidth
                disabled
              />
            )}
          />
          {errors?.currencyId?.message && (
            <div className={cn.error}>{errors?.currencyId?.message}</div>
          )}
        </>
      </ItemFormBlock>
      <ItemFormBlock title="Individual customer">
        <>
          <ItemFormFlexInputs
            title="Single transaction"
            control={control}
            errorFrom={errors.individualSingleTransactionFrom?.message}
            errorTo={errors.individualSingleTransactionTo?.message}
            nameFrom="individualSingleTransactionFrom"
            nameTo="individualSingleTransactionTo"
            disabled={Boolean(!isEditable && itemId)}
            baseCurrency={baseCurrency}
            currencyId={currencyId}
            currencies={currencies}
            currenciesList={currenciesList}
          />
          <ItemFormFlexInputs
            title="Multi transaction"
            control={control}
            errorFrom={errors.individualMultiTransactionFrom?.message}
            errorTo={errors.individualMultiTransactionTo?.message}
            nameFrom="individualMultiTransactionFrom"
            nameTo="individualMultiTransactionTo"
            disabled={Boolean(!isEditable && itemId)}
            baseCurrency={baseCurrency}
            currencyId={currencyId}
            currencies={currencies}
            currenciesList={currenciesList}
          />
        </>
      </ItemFormBlock>
      <ItemFormBlock title="Corporate customer">
        <>
          <ItemFormFlexInputs
            title="Single transaction"
            control={control}
            errorFrom={errors.corporateSingleTransactionFrom?.message}
            errorTo={errors.corporateSingleTransactionTo?.message}
            nameFrom="corporateSingleTransactionFrom"
            nameTo="corporateSingleTransactionTo"
            disabled={Boolean(!isEditable && itemId)}
            baseCurrency={baseCurrency}
            currencyId={currencyId}
            currencies={currencies}
            currenciesList={currenciesList}
          />
          <ItemFormFlexInputs
            title="Multi transaction"
            control={control}
            errorFrom={errors.corporateMultiTransactionFrom?.message}
            errorTo={errors.corporateMultiTransactionTo?.message}
            nameFrom="corporateMultiTransactionFrom"
            nameTo="corporateMultiTransactionTo"
            disabled={Boolean(!isEditable && itemId)}
            baseCurrency={baseCurrency}
            currencyId={currencyId}
            currencies={currencies}
            currenciesList={currenciesList}
          />
        </>
      </ItemFormBlock>
    </div>
  );
};

export default ItemForm;
